
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";

export default {
    spacingDebug: false,
    props: {
        branches: { type: Array, default: () => [] },
        cluster: { type: Object, default: null },
        searching: { type: Boolean, default: false },
        interactive: { type: Boolean, default: true },
        animateMarkers: { type: Boolean, default: false },
        activeId: { type: String, default: null },
        overlayWidth: { type: Number, default: 0 },
    },
    data() {
        return { status: "loading", tileError: false, markerAnimationTimer: null, markerAnimationIndex: 0 };
    },
    computed: {
        canvasLabel() {
            return this.interactive ? "Branch locations. Use arrow keys to pan and plus or minus to zoom." : "Branch locations preview";
        },
        statusMessage() {
            return this.status === "error" ? "The map could not load. You can still browse the branch list." : "";
        },
    },
    watch: {
        cluster() {
            this.showCluster();
        },
        searching() {
            this.updateSelection();
        },
        branches() {
            this.renderMarkers();
        },
        activeId() {
            this.updateSelection();
        },
        overlayWidth() {
            this.resizeMap();
        },
    },
    mounted() {
        this.initializeMap();
    },
    beforeDestroy() {
        this.destroyMap();
    },
    methods: {
        async initializeMap() {
            this.destroyMap();
            this.status = "loading";
            this.tileError = false;
            try {
                const module = await import("leaflet");
                if (this._isDestroyed || this._isBeingDestroyed) return;
                await import("@maplibre/maplibre-gl-leaflet");
                if (this._isDestroyed || this._isBeingDestroyed) return;
                this.leaflet = module.default || module;
                this.map = this.leaflet.map(this.$refs.canvas, {
                    zoomControl: false,
                    scrollWheelZoom: false,
                    dragging: this.interactive,
                    doubleClickZoom: this.interactive,
                    touchZoom: this.interactive,
                    boxZoom: this.interactive,
                    keyboard: this.interactive,
                    minZoom: 4,
                    maxZoom: 18,
                });
                this.showCluster();
                if (this.interactive) this.leaflet.control.zoom({ position: "bottomleft" }).addTo(this.map);
                this.map.attributionControl.addAttribution(
                    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
                );
                this.basemap = this.leaflet.maplibreGL({ style: "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json" }).addTo(this.map);
                const vectorMap = this.basemap.getMaplibreMap();
                vectorMap.on("style.load", () => {
                    if (!this.map) return;
                    vectorMap.getStyle().layers.forEach((layer) => {
                        if (layer["source-layer"] === "boundary") vectorMap.setLayoutProperty(layer.id, "visibility", "none");
                    });
                    this.status = "ready";
                });
                vectorMap.on("error", () => {
                    this.tileError = true;
                    if (this.status === "loading") this.status = "error";
                });
                this.markerLayer = this.leaflet.layerGroup().addTo(this.map);
                this.renderMarkers();
                if (window.ResizeObserver) {
                    this.mapObserver = new ResizeObserver(this.resizeMap);
                    this.mapObserver.observe(this.$refs.canvas);
                }
            } catch (_) {
                this.destroyMap();
                this.status = "error";
            }
        },
        destroyMap() {
            this.clearMarkerAnimation();
            if (this.mapObserver) this.mapObserver.disconnect();
            if (this.map) this.map.remove();
            this.map = null;
            this.basemap = null;
            this.markerLayer = null;
            this.markers = null;
        },
        mapPadding() {
            return { paddingTopLeft: [40, 72], paddingBottomRight: [this.overlayWidth + 32, 56] };
        },
        renderMarkers() {
            if (!this.map || !this.markerLayer) return;
            this.clearMarkerAnimation();
            this.markerLayer.clearLayers();
            this.markers = new Map();
            this.markerAnimationIndex = 0;
            if (this.animateMarkers) {
                this.revealNextMarker();
                return;
            }
            this.branches.forEach((branch) => {
                this.addMarker(branch);
            });
            this.updateSelection();
        },
        addMarker(branch) {
            const badge = document.createElement("span");
            badge.id = `38e624dd-1c62-4d66-92c5-6058b6a92a49-${branch.id}`;
            badge.className = this.attr.pin_core;
            badge.textContent = String(branch.sequence);
            const marker = this.leaflet.marker([branch.latitude, branch.longitude], {
                title: branch.name,
                alt: `Branch ${branch.sequence}: ${branch.name}`,
                keyboard: this.interactive,
                icon: this.leaflet.divIcon({ className: this.attr.pin, html: badge, iconSize: [56, 56], iconAnchor: [28, 28] }),
            });
            const label = document.createElement("span");
            label.id = `fda2a370-052e-47e3-a2ac-2a709d80b138-${branch.id}`;
            label.textContent = branch.name;
            if (this.interactive) {
                marker.bindTooltip(label, { direction: "top", offset: [0, -30] });
                marker.on("click", () => this.$emit("select", branch.id));
            }
            marker.addTo(this.markerLayer);
            this.markers.set(branch.id, marker);
        },
        revealNextMarker() {
            if (!this.map || !this.markerLayer || !this.branches.length) return;
            if (this.markerAnimationIndex >= this.branches.length) {
                this.markerAnimationTimer = setTimeout(() => {
                    if (!this.map || !this.markerLayer) return;
                    this.markerLayer.clearLayers();
                    this.markers = new Map();
                    this.markerAnimationIndex = 0;
                    this.revealNextMarker();
                }, 3000);
                return;
            }
            this.addMarker(this.branches[this.markerAnimationIndex]);
            this.markerAnimationIndex++;
            this.fitAnimatedBranches();
            this.markerAnimationTimer = setTimeout(() => this.revealNextMarker(), 3000);
        },
        fitAnimatedBranches() {
            if (!this.map || !this.markerAnimationIndex) return;
            const revealedBranches = this.branches.slice(0, this.markerAnimationIndex);
            if (revealedBranches.length === 1) {
                const firstBranch = revealedBranches[0];
                this.map.setView([firstBranch.latitude, firstBranch.longitude], 12, { animate: false });
                return;
            }
            this.map.fitBounds(
                revealedBranches.map((branch) => [branch.latitude, branch.longitude]),
                {
                    ...this.mapPadding(),
                    maxZoom: Math.max(5, 12 - Math.log2(revealedBranches.length)),
                    animate: true,
                    duration: 2.5,
                },
            );
        },
        clearMarkerAnimation() {
            if (this.markerAnimationTimer !== null) clearTimeout(this.markerAnimationTimer);
            this.markerAnimationTimer = null;
        },
        updateSelection() {
            if (!this.map || !this.markers) return;
            this.markers.forEach((marker, id) => {
                const element = marker.getElement();
                if (element) {
                    element.classList.toggle(this.attr.selected, id === this.activeId);
                    element.setAttribute("aria-pressed", id === this.activeId ? "true" : "false");
                }
                marker.setZIndexOffset(id === this.activeId ? 1000 : 0);
            });
            if (this.activeId) this.focusBranch(this.activeId);
            else if (this.searching) this.fitBranches();
            else this.showCluster();
        },
        focusBranch(id) {
            if (!this.map) return;
            const branch = this.branches.find((item) => item.id === id);
            if (!branch) return;
            const point = [branch.latitude, branch.longitude];
            this.map.fitBounds([point, point], { ...this.mapPadding(), maxZoom: 15, animate: false });
        },
        fitBranches() {
            if (!this.map) return;
            if (!this.branches.length) {
                this.map.setView([12.2, 122.5], 5);
                return;
            }
            this.map.fitBounds(
                this.branches.map((branch) => [branch.latitude, branch.longitude]),
                { ...this.mapPadding(), maxZoom: this.branches.length === 1 ? 15 : 7, animate: false },
            );
        },
        showCluster() {
            if (!this.map) return;
            if (this.animateMarkers && this.markerAnimationIndex) {
                this.fitAnimatedBranches();
                return;
            }
            const center = this.cluster?.center || [12.2, 122.5];
            const zoom = this.cluster?.zoom || 6;
            this.map.setView(center, zoom, { animate: false });
            if (this.overlayWidth) this.map.panBy([this.overlayWidth / 2, 0], { animate: false });
        },
        resizeMap() {
            if (!this.map) return;
            this.map.invalidateSize({ pan: false });
            if (this.activeId) this.focusBranch(this.activeId);
            else if (this.searching) this.fitBranches();
            else this.showCluster();
        },
    },
};
